/**
 * PaymentStatusPanel
 * Shown inside the Deal Room when the deal is in any escrow status.
 * Displays all invoices for the deal with role-aware action buttons:
 *   - Payer:  "Fund Escrow" / "Release Payment" / "Raise Dispute"
 *   - Payee:  "Request Release" (when escrow is funded)
 *   - Both:   read-only badges when disputed / released / completed
 */

import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Alert, AlertDescription } from './ui/alert';
import { toast } from 'sonner';
import {
    DollarSign, Lock, CheckCircle, AlertCircle, Clock,
    ArrowRightCircle, Loader2, ShieldAlert, ExternalLink
} from 'lucide-react';
import api from '../lib/api';
import { useTranslation } from '../i18n';

// ── Escrow status display config ─────────────────────────────────────────────

const ESCROW_STATUS_CONFIG = {
    pending: {
        label: 'Pending Payment',
        color: 'bg-yellow-100 text-yellow-700 border-yellow-300',
        icon: Clock,
    },
    checkout_initiated: {
        label: 'Payment Started',
        color: 'bg-blue-100 text-blue-700 border-blue-300',
        icon: ArrowRightCircle,
    },
    funded: {
        label: 'In Escrow',
        color: 'bg-blue-100 text-blue-700 border-blue-300',
        icon: Lock,
    },
    release_requested: {
        label: 'Release Requested',
        color: 'bg-indigo-100 text-indigo-700 border-indigo-300',
        icon: ArrowRightCircle,
    },
    release_approved: {
        label: 'Release Approved',
        color: 'bg-emerald-100 text-emerald-700 border-emerald-300',
        icon: CheckCircle,
    },
    released: {
        label: 'Released',
        color: 'bg-green-100 text-green-700 border-green-300',
        icon: CheckCircle,
    },
    disputed: {
        label: 'Disputed',
        color: 'bg-orange-100 text-orange-700 border-orange-300',
        icon: AlertCircle,
    },
    failed: {
        label: 'Payment Failed',
        color: 'bg-red-100 text-red-700 border-red-300',
        icon: AlertCircle,
    },
};

export function EscrowBadge({ status }) {
    const cfg = ESCROW_STATUS_CONFIG[status] || ESCROW_STATUS_CONFIG.pending;
    const Icon = cfg.icon;
    return (
        <Badge variant="outline" className={`${cfg.color} flex items-center gap-1.5 px-2 py-1`}>
            <Icon className="h-3.5 w-3.5" />
            <span className="text-xs font-medium">{cfg.label}</span>
        </Badge>
    );
}

// ── Format helpers ────────────────────────────────────────────────────────────

function formatAmount(amount, currency) {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency || 'USD',
        minimumFractionDigits: 2,
    }).format(amount || 0);
}

// ── Main Component ─────────────────────────────────────────────────────────────

export default function PaymentStatusPanel({ invoices = [], currentUser, deal, clients = [], onRefresh }) {
    const queryClient = useQueryClient();
    const [disputeModalOpen, setDisputeModalOpen] = useState(false);
    const [disputeInvoiceId, setDisputeInvoiceId] = useState(null);
    const [disputeReason, setDisputeReason] = useState('');
    const [resolveModalOpen, setResolveModalOpen] = useState(false);
    const [resolveInvoiceId, setResolveInvoiceId] = useState(null);
    const [resolveReason, setResolveReason] = useState('');
    const [confirmReleaseInvoiceId, setConfirmReleaseInvoiceId] = useState(null);

    const dealId = deal?._id;
    const currentUserId = String(currentUser?._id || currentUser?.id || '');

    // Build the set of client IDs that belong to the current logged-in user.
    // A client is "owned" by the current user when client.linked_user_id === currentUserId.
    // This allows us to correctly identify payer/payee for client-type participants.
    const myClientIds = new Set(
        (clients || []).filter(c => String(c.linked_user_id) === currentUserId).map(c => String(c._id))
    );

    function isCurrentUserPayer(invoice) {
        if (invoice.payer_type === 'user') {
            return invoice.payer_id === currentUserId;
        }
        if (invoice.payer_type === 'client') {
            return myClientIds.has(String(invoice.payer_id));
        }
        return false;
    }

    function isCurrentUserPayee(invoice) {
        if (invoice.payee_type === 'user') {
            return invoice.payee_id === currentUserId;
        }
        if (invoice.payee_type === 'client') {
            return myClientIds.has(String(invoice.payee_id));
        }
        return false;
    }

    // ── Fund Escrow ──────────────────────────────────────────────────────────

    const fundEscrowMutation = useMutation({
        mutationFn: (invoiceId) => api.post(`/payments/checkout/${invoiceId}`),
        onSuccess: (data) => {
            if (data?.checkout_url) {
                window.location.href = data.checkout_url;
            } else {
                toast.error('Could not redirect to payment page');
            }
        },
        onError: (err) => {
            toast.error(err?.response?.data?.detail || 'Failed to initiate payment');
        },
    });

    // ── Release Escrow ───────────────────────────────────────────────────────

    const releaseMutation = useMutation({
        mutationFn: (invoiceId) => api.post(`/payments/release/${invoiceId}`),
        onSuccess: (data, invoiceId) => {
            // Immediately update the invoice card — no wait for refetch
            queryClient.setQueryData(['deal-invoices', dealId], (old) => {
                if (!old) return old;
                const patchInv = (inv) => String(inv._id) !== String(invoiceId) ? inv : {
                    ...inv,
                    escrow_status: 'released',
                };
                if (Array.isArray(old)) return old.map(patchInv);
                return { ...old, invoices: (old.invoices || []).map(patchInv) };
            });
            // Background sync for consistency
            queryClient.invalidateQueries(['deal-invoices', dealId]);
            queryClient.invalidateQueries(['deal', dealId]);
            setConfirmReleaseInvoiceId(null);
            toast.success('Payment released to payee successfully!');
            onRefresh?.();
        },
        onError: (err) => {
            toast.error(err?.response?.data?.detail || 'Failed to release payment');
        },
    });

    // ── Request Release ──────────────────────────────────────────────────────

    const requestReleaseMutation = useMutation({
        mutationFn: (invoiceId) => api.post(`/payments/request-release/${invoiceId}`),
        onSuccess: (data, invoiceId) => {
            // Immediately update the invoice card — no wait for refetch
            queryClient.setQueryData(['deal-invoices', dealId], (old) => {
                if (!old) return old;
                const patchInv = (inv) => String(inv._id) !== String(invoiceId) ? inv : {
                    ...inv,
                    escrow_status: 'release_requested',
                };
                if (Array.isArray(old)) return old.map(patchInv);
                return { ...old, invoices: (old.invoices || []).map(patchInv) };
            });
            // Background sync for consistency
            queryClient.invalidateQueries(['deal-invoices', dealId]);
            toast.success('Release request sent to payer!');
            onRefresh?.();
        },
        onError: (err) => {
            toast.error(err?.response?.data?.detail || 'Failed to send release request');
        },
    });

    // ── Dispute ──────────────────────────────────────────────────────────────

    const disputeMutation = useMutation({
        mutationFn: ({ invoiceId, reason }) =>
            api.post(`/payments/dispute/${invoiceId}`, { reason }),
        onSuccess: (data, { invoiceId, reason }) => {
            // Immediately update the specific invoice in cache — no wait for refetch
            queryClient.setQueryData(['deal-invoices', dealId], (old) => {
                if (!old) return old;
                const patchInv = (inv) => String(inv._id) !== String(invoiceId) ? inv : {
                    ...inv,
                    escrow_status: 'disputed',
                    dispute_count: (inv.dispute_count || 0) + 1,
                    dispute_history: [
                        ...(inv.dispute_history || []),
                        { type: 'disputed', reason, timestamp: new Date().toISOString() }
                    ],
                };
                // Cache shape is { invoices: [...] } (raw API response)
                if (Array.isArray(old)) return old.map(patchInv);
                return { ...old, invoices: (old.invoices || []).map(patchInv) };
            });
            // Background sync for consistency
            queryClient.invalidateQueries(['deal-invoices', dealId]);
            queryClient.invalidateQueries(['deal', dealId]);
            setDisputeModalOpen(false);
            setDisputeReason('');
            setDisputeInvoiceId(null);
            toast.success('Dispute raised. Platform support has been notified.');
            onRefresh?.();
        },
        onError: (err) => {
            toast.error(err?.response?.data?.detail || 'Failed to raise dispute');
        },
    });

    // ── Resolve Dispute (Payee Only) ─────────────────────────────────────────

    const resolveDisputeMutation = useMutation({
        mutationFn: ({ invoiceId, reason }) =>
            api.post(`/payments/dispute/${invoiceId}/resolve`, { reason }),
        onSuccess: (data, { invoiceId, reason }) => {
            // Immediately update the specific invoice in cache
            queryClient.setQueryData(['deal-invoices', dealId], (old) => {
                if (!old) return old;
                const patchInv = (inv) => String(inv._id) !== String(invoiceId) ? inv : {
                    ...inv,
                    escrow_status: 'funded',
                    dispute_history: [
                        ...(inv.dispute_history || []),
                        { type: 'resolved', reason, timestamp: new Date().toISOString() }
                    ],
                };
                // Cache shape is { invoices: [...] } (raw API response)
                if (Array.isArray(old)) return old.map(patchInv);
                return { ...old, invoices: (old.invoices || []).map(patchInv) };
            });
            // Background sync for consistency
            queryClient.invalidateQueries(['deal-invoices', dealId]);
            queryClient.invalidateQueries(['deal', dealId]);
            setResolveModalOpen(false);
            setResolveReason('');
            setResolveInvoiceId(null);
            toast.success('Dispute resolved. Payment is ready for release.');
            onRefresh?.();
        },
        onError: (err) => {
            toast.error(err?.response?.data?.detail || 'Failed to resolve dispute');
        },
    });

    // ── Withdraw Payment (payee on-demand onboarding) ─────────────────────

    const withdrawMutation = useMutation({
        mutationFn: (invoiceId) => api.post(`/payments/withdraw/${invoiceId}`),
        onSuccess: (data) => {
            if (data?.onboarding_url) {
                // Redirect to Stripe Express onboarding page
                window.location.href = data.onboarding_url;
            } else if (data?.transferred) {
                // Already onboarded — transfer happened immediately
                queryClient.invalidateQueries(['deal-invoices', dealId]);
                queryClient.invalidateQueries(['deal', dealId]);
                toast.success('Payment withdrawn successfully!');
                onRefresh?.();
            }
        },
        onError: (err) => {
            toast.error(err?.response?.data?.detail || 'Failed to initiate withdrawal');
        },
    });

    const openDisputeModal = (invoiceId) => {
        setDisputeInvoiceId(invoiceId);
        setDisputeReason('');
        setDisputeModalOpen(true);
    };

    const openResolveModal = (invoiceId) => {
        setResolveInvoiceId(invoiceId);
        setResolveReason('');
        setResolveModalOpen(true);
    };

    const releasedCount = invoices.filter(i => i.escrow_status === 'released').length;
    const fundedCount = invoices.filter(i => ['funded', 'release_requested', 'release_approved'].includes(i.escrow_status)).length;
    const totalCount = invoices.length;

    // ── Render ───────────────────────────────────────────────────────────────

    if (invoices.length === 0) return null;

    return (
        <>
            <Card className="border-2 border-blue-200 bg-blue-50/40 dark:bg-blue-950/20 dark:border-blue-900">
                <CardHeader className="pb-3">
                    <CardTitle className="flex items-center justify-between text-base">
                        <div className="flex items-center gap-2">
                            <Lock className="h-4 w-4 text-blue-600" />
                            <span>Escrow Payments</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-xs bg-white dark:bg-card">
                                {releasedCount}/{totalCount} released
                            </Badge>
                            {fundedCount > 0 && (
                                <Badge variant="outline" className="text-xs bg-blue-100 text-blue-700 border-blue-300">
                                    {fundedCount} in escrow
                                </Badge>
                            )}
                        </div>
                    </CardTitle>
                </CardHeader>

                <CardContent className="space-y-3">
                    {invoices.map((invoice) => {
                        const isPayer = isCurrentUserPayer(invoice);
                        const isPayee = isCurrentUserPayee(invoice);
                        const escrowStatus = invoice.escrow_status || 'pending';
                        const isFunding = fundEscrowMutation.isPending;
                        const isReleasing = releaseMutation.isPending && confirmReleaseInvoiceId === invoice._id;
                        const isRequesting = requestReleaseMutation.isPending;
                        const isDisputing = disputeMutation.isPending && disputeInvoiceId === invoice._id;
                        const isResolving = resolveDisputeMutation.isPending && resolveInvoiceId === invoice._id;
                        const isWithdrawing = withdrawMutation.isPending;

                        // Check if THIS specific invoice is currently undergoing any mutation
                        const isProcessing =
                            (fundEscrowMutation.isPending && /* no way to track specific invoice id for funding without adding state, but we'll assume global for now or skip */ false) ||
                            (isReleasing) ||
                            (isDisputing) ||
                            (isResolving);

                        return (
                            <div
                                key={invoice._id}
                                className="relative rounded-lg border bg-white dark:bg-card p-4 space-y-3 overflow-hidden"
                            >
                                {/* Loading Overlay */}
                                {isProcessing && (
                                    <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60 dark:bg-black/40 backdrop-blur-sm">
                                        <div className="flex flex-col items-center gap-2 text-primary">
                                            <Loader2 className="h-6 w-6 animate-spin" />
                                            <span className="text-sm font-medium">Processing...</span>
                                        </div>
                                    </div>
                                )}

                                {/* Invoice header row */}
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0 flex-1">
                                        <p className="font-medium text-sm truncate">
                                            {invoice.invoice_number}
                                        </p>
                                        <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
                                            <span className="font-medium text-red-600">{invoice.payer_name || 'Payer'}</span>
                                            <span>→</span>
                                            <span className="font-medium text-emerald-600">{invoice.payee_name || 'Payee'}</span>
                                        </p>
                                        {/* {invoice.escrow_status === 'disputed' && invoice.dispute_reason && (
                                            <p className="text-xs text-orange-600 mt-1 flex items-center gap-1">
                                                <AlertCircle className="h-3 w-3 shrink-0" />
                                                Currently Disputed: {invoice.dispute_reason}
                                            </p>
                                        )} */}
                                    </div>
                                    <div className="flex flex-col items-end gap-1.5 shrink-0">
                                        <p className="text-base font-bold">
                                            {formatAmount(invoice.total, invoice.currency)}
                                        </p>
                                        <EscrowBadge status={escrowStatus} />
                                    </div>
                                </div>

                                {/* Platform fee note */}
                                {invoice.platform_fee_amount > 0 && escrowStatus !== 'released' && (
                                    <p className="text-[11px] text-muted-foreground">
                                        Platform fee: {formatAmount(invoice.platform_fee_amount, invoice.currency)} — payee receives {formatAmount(invoice.total - invoice.platform_fee_amount, invoice.currency)}
                                    </p>
                                )}

                                {/* Dispute History Timeline */}
                                {invoice.dispute_history && invoice.dispute_history.length > 0 && (
                                    <div className="bg-slate-50 dark:bg-slate-900/50 rounded-md p-3 text-xs space-y-2 border border-slate-100 dark:border-slate-800">
                                        <p className="font-semibold text-slate-700 dark:text-slate-300">Dispute History</p>
                                        <ul className="space-y-2 relative border-l border-slate-200 dark:border-slate-700 ml-2 pl-3">
                                            {invoice.dispute_history.map((hist, idx) => {
                                                const isDisputed = hist.type === 'disputed';

                                                // Calculate dispute pairing number (1, 2, 3...)
                                                // E.g. Disputed #1, Resolved #1, Disputed #2...
                                                let disputeNum = 1;
                                                for (let i = 0; i <= idx; i++) {
                                                    if (invoice.dispute_history[i].type === 'disputed' && i < idx) {
                                                        disputeNum++;
                                                    }
                                                }

                                                return (
                                                    <li key={idx} className="relative">
                                                        <div className={`absolute -left-[17px] top-1 h-2 w-2 rounded-full border border-white dark:border-card ${isDisputed ? 'bg-orange-500' : 'bg-emerald-500'}`} />
                                                        <span className={`font-semibold ${isDisputed ? 'text-orange-600' : 'text-emerald-600'}`}>
                                                            {isDisputed ? `Dispute ${disputeNum}` : `Resolved explanation ${disputeNum - 1}`}
                                                        </span>
                                                        <span className="text-muted-foreground ml-2">
                                                            {new Date(hist.timestamp).toLocaleDateString()}
                                                        </span>
                                                        <p className="text-slate-600 dark:text-slate-400 mt-0.5 whitespace-pre-wrap">{hist.reason}</p>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    </div>
                                )}

                                {/* Action buttons */}
                                <div className="flex flex-wrap gap-2">
                                    {/* PAYER: Fund Escrow (when pending or failed) */}
                                    {isPayer && ['pending', 'failed', 'checkout_initiated'].includes(escrowStatus) && (
                                        <Button
                                            size="sm"
                                            className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                            onClick={() => fundEscrowMutation.mutate(invoice._id)}
                                            disabled={isFunding}
                                            data-testid={`fund-escrow-${invoice._id}`}
                                        >
                                            {isFunding ? (
                                                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                                            ) : (
                                                <DollarSign className="h-3.5 w-3.5 mr-1.5" />
                                            )}
                                            {escrowStatus === 'checkout_initiated' ? 'Resume Payment' : 'Fund Escrow'}
                                            <ExternalLink className="h-3 w-3 ml-1.5 opacity-60" />
                                        </Button>
                                    )}

                                    {/* PAYER: Release Payment (when funded or release_requested) */}
                                    {isPayer && ['funded', 'release_requested'].includes(escrowStatus) && (
                                        <Button
                                            size="sm"
                                            className="bg-blue-600 hover:bg-blue-700 text-white"
                                            onClick={() => setConfirmReleaseInvoiceId(invoice._id)}
                                            disabled={isReleasing}
                                            data-testid={`release-payment-${invoice._id}`}
                                        >
                                            {isReleasing ? (
                                                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                                            ) : (
                                                <CheckCircle className="h-3.5 w-3.5 mr-1.5" />
                                            )}
                                            Release Payment
                                        </Button>
                                    )}

                                    {/* PAYER: Raise Dispute (when funded / release_requested / dispute_count < 5) */}
                                    {isPayer && ['funded', 'release_requested'].includes(escrowStatus) && (invoice.dispute_count || 0) < 5 && (
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            className="border-orange-300 text-orange-600 hover:bg-orange-100 hover:text-orange-700 hover:border-orange-400"
                                            onClick={() => openDisputeModal(invoice._id)}
                                            disabled={isDisputing}
                                            data-testid={`dispute-${invoice._id}`}
                                        >
                                            <ShieldAlert className="h-3.5 w-3.5 mr-1.5" />
                                            Raise Dispute ({(invoice.dispute_count || 0)}/5)
                                        </Button>
                                    )}

                                    {/* PAYER: Dispute Limit Reached */}
                                    {isPayer && ['funded', 'release_requested'].includes(escrowStatus) && (invoice.dispute_count || 0) >= 5 && (
                                        <Badge variant="outline" className="border-red-300 text-red-600 bg-red-50 text-xs">
                                            <ShieldAlert className="h-3 w-3 mr-1" />
                                            Maximum disputes (5/5) reached. Please communicate or release payment.
                                        </Badge>
                                    )}

                                    {/* PAYEE: Request Release (when funded) */}
                                    {isPayee && escrowStatus === 'funded' && (
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            className="border-blue-300 text-blue-600 hover:bg-blue-100 hover:text-blue-700 hover:border-blue-400"
                                            onClick={() => requestReleaseMutation.mutate(invoice._id)}
                                            disabled={isRequesting}
                                            data-testid={`request-release-${invoice._id}`}
                                        >
                                            {isRequesting ? (
                                                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                                            ) : (
                                                <ArrowRightCircle className="h-3.5 w-3.5 mr-1.5" />
                                            )}
                                            Request Release
                                        </Button>
                                    )}

                                    {/* PAYEE: Awaiting payer (when release_requested) */}
                                    {isPayee && escrowStatus === 'release_requested' && (
                                        <Badge variant="outline" className="border-indigo-300 text-indigo-600 bg-indigo-50 text-xs">
                                            <Clock className="h-3 w-3 mr-1" />
                                            Awaiting payer approval
                                        </Badge>
                                    )}

                                    {/* PAYEE: Awaiting payment */}
                                    {isPayee && ['pending', 'checkout_initiated'].includes(escrowStatus) && (
                                        <Badge variant="outline" className="border-yellow-300 text-yellow-600 bg-yellow-50 text-xs">
                                            <Clock className="h-3 w-3 mr-1" />
                                            Awaiting payment from payer
                                        </Badge>
                                    )}

                                    {/* PAYER: Payment Release Approved — awaiting payee withdrawal */}
                                    {isPayer && escrowStatus === 'release_approved' && (
                                        <Badge variant="outline" className="border-emerald-400 text-emerald-700 bg-emerald-50 text-xs">
                                            <CheckCircle className="h-3 w-3 mr-1" />
                                            Payment Released — Awaiting Payee Withdrawal
                                        </Badge>
                                    )}

                                    {/* PAYEE: Withdraw Payment button */}
                                    {isPayee && escrowStatus === 'release_approved' && (
                                        <Button
                                            size="sm"
                                            className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                            onClick={() => withdrawMutation.mutate(invoice._id)}
                                            disabled={withdrawMutation.isPending}
                                        >
                                            {withdrawMutation.isPending ? (
                                                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                                            ) : (
                                                <ArrowRightCircle className="h-3.5 w-3.5 mr-1.5" />
                                            )}
                                            Withdraw to Bank Account
                                        </Button>
                                    )}

                                    {/* PAYER: Disputed State */}
                                    {isPayer && escrowStatus === 'disputed' && (
                                        <Badge variant="outline" className="border-red-300 text-red-600 bg-red-50 text-xs">
                                            <ShieldAlert className="h-3 w-3 mr-1" />
                                            Disputed  — Awaiting Payee Resolution
                                        </Badge>
                                    )}

                                    {/* PAYEE: Resolve Dispute */}
                                    {isPayee && escrowStatus === 'disputed' && (
                                        <Button
                                            size="sm"
                                            className="bg-orange-500 hover:bg-orange-600 text-white"
                                            onClick={() => openResolveModal(invoice._id)}
                                            disabled={isResolving}
                                        >
                                            {isResolving ? (
                                                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                                            ) : (
                                                <CheckCircle className="h-3.5 w-3.5 mr-1.5" />
                                            )}
                                            Resolve Dispute
                                        </Button>
                                    )}
                                </div>
                            </div>
                        );
                    })}

                    {/* Summary note */}
                    {releasedCount === totalCount && totalCount > 0 && (
                        <Alert className="border-green-300 bg-green-50 dark:bg-green-950/20">
                            <CheckCircle className="h-4 w-4 text-green-600" />
                            <AlertDescription className="text-green-700 text-sm font-medium">
                                All escrow payments have been released. Deal is complete.
                            </AlertDescription>
                        </Alert>
                    )}
                </CardContent>
            </Card>

            {/* ── Confirm Release Dialog ── */}
            <Dialog
                open={!!confirmReleaseInvoiceId}
                onOpenChange={(v) => !v && setConfirmReleaseInvoiceId(null)}
            >
                <DialogContent className="max-w-sm">
                    <DialogHeader>
                        <DialogTitle>Release Escrow Funds?</DialogTitle>
                        <DialogDescription>
                            This will immediately transfer the funds (minus platform fee) to the payee's account. This action cannot be undone.
                        </DialogDescription>
                    </DialogHeader>
                    {confirmReleaseInvoiceId && (() => {
                        const inv = invoices.find(i => i._id === confirmReleaseInvoiceId);
                        if (!inv) return null;
                        return (
                            <div className="py-2 space-y-2 text-sm">
                                <div className="flex justify-between">
                                    <span className="text-muted-foreground">Payee</span>
                                    <span className="font-medium">{inv.payee_name}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-muted-foreground">Amount to transfer</span>
                                    <span className="font-medium text-emerald-600">
                                        {formatAmount(inv.total - (inv.platform_fee_amount || 0), inv.currency)}
                                    </span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-muted-foreground">Platform fee</span>
                                    <span className="text-muted-foreground">
                                        {formatAmount(inv.platform_fee_amount || 0, inv.currency)}
                                    </span>
                                </div>
                            </div>
                        );
                    })()}
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setConfirmReleaseInvoiceId(null)}
                            disabled={releaseMutation.isPending}
                        >
                            Cancel
                        </Button>
                        <Button
                            className="bg-blue-600 hover:bg-blue-700 text-white"
                            onClick={() => releaseMutation.mutate(confirmReleaseInvoiceId)}
                            disabled={releaseMutation.isPending}
                        >
                            {releaseMutation.isPending ? (
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                                <CheckCircle className="h-4 w-4 mr-2" />
                            )}
                            Yes, Release Funds
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* ── Raise Dispute Dialog ── */}
            <Dialog
                open={disputeModalOpen}
                onOpenChange={(v) => !v && setDisputeModalOpen(false)}
            >
                <DialogContent className="max-w-sm">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-orange-600">
                            <ShieldAlert className="h-5 w-5" />
                            Raise a Dispute
                        </DialogTitle>
                        <DialogDescription>
                            Explain the issue. Platform support will review and contact both parties.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-2 space-y-2">
                        <Label>Reason for dispute <span className="text-destructive">*</span></Label>
                        <Textarea
                            placeholder="Describe the issue clearly (e.g. work not completed as agreed, incorrect deliverable...)"
                            rows={4}
                            value={disputeReason}
                            onChange={(e) => setDisputeReason(e.target.value)}
                        />
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setDisputeModalOpen(false)}
                            disabled={disputeMutation.isPending}
                        >
                            Cancel
                        </Button>
                        <Button
                            className="bg-orange-500 hover:bg-orange-600 text-white"
                            onClick={() => {
                                if (!disputeReason.trim()) return toast.error('Please provide a reason');
                                disputeMutation.mutate({ invoiceId: disputeInvoiceId, reason: disputeReason });
                            }}
                            disabled={disputeMutation.isPending}
                        >
                            {disputeMutation.isPending ? (
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                                <ShieldAlert className="h-4 w-4 mr-2" />
                            )}
                            Submit Dispute
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* ── Resolve Dispute Dialog ── */}
            <Dialog
                open={resolveModalOpen}
                onOpenChange={(v) => !v && setResolveModalOpen(false)}
            >
                <DialogContent className="max-w-sm">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-emerald-600">
                            <CheckCircle className="h-5 w-5" />
                            Resolve Dispute
                        </DialogTitle>
                        <DialogDescription>
                            Explain how the issue was resolved. The funds will be un-frozen so the payer can release them.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-2 space-y-2">
                        <Label>Resolution Explanation <span className="text-destructive">*</span></Label>
                        <Textarea
                            placeholder="Describe how the dispute was handled (e.g. delivered missing files, provided a discount...)"
                            rows={4}
                            value={resolveReason}
                            onChange={(e) => setResolveReason(e.target.value)}
                        />
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setResolveModalOpen(false)}
                            disabled={resolveDisputeMutation.isPending}
                        >
                            Cancel
                        </Button>
                        <Button
                            className="bg-emerald-600 hover:bg-emerald-700 text-white"
                            onClick={() => {
                                if (!resolveReason.trim()) return toast.error('Please provide a resolution explanation');
                                resolveDisputeMutation.mutate({ invoiceId: resolveInvoiceId, reason: resolveReason });
                            }}
                            disabled={resolveDisputeMutation.isPending}
                        >
                            {resolveDisputeMutation.isPending ? (
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                                <CheckCircle className="h-4 w-4 mr-2" />
                            )}
                            Resolve and Unfreeze
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
